// -----------------------------------------------------------------------------
// Intake API Types (snake_case)
// -----------------------------------------------------------------------------
// NOTE: These interfaces mirror the JSON payloads used by the Django backend.
// All property names stay EXACTLY the same (snake_case) so that
// `fetch(`/api/intakes`)` responses can be typed directly with no mapping.
//
// React components should *not* import these directly.  Instead they should work
// with the camelCase structures defined in `form/types.ts`, `form/formatter.ts`, and `form/initialForm.ts`, which
// converts to/from this backend shape.
// -----------------------------------------------------------------------------

import { CompanionMember } from './companions-types';

// -----------------------------------------------------------------------------
// Intake Status Enum (MUST match backend: app/defs/intakes.py::IntakeStatusEnum)
// -----------------------------------------------------------------------------

/**
 * Intake status enum values matching backend IntakeStatusEnum
 *
 * Single source of truth for all intake status values across the frontend.
 */
export const IntakeStatus = {
  IN_PROGRESS: 'in_progress',
  SENT_FOR_SIGNATURE: 'sent_for_signature',
  SIGNED: 'signed',
  ARCHIVED: 'archived',
  REJECTED: 'rejected',
  CHASE: 'chase',
  LOST: 'lost',
  ESCALATED: 'escalated',
} as const;

/**
 * Handler enum - designates who is responsible for follow-ups
 */
export const IntakeHandler = {
  FINCH: 'finch',
  FIRM: 'firm',
} as const;

export type IntakeHandlerValue =
  (typeof IntakeHandler)[keyof typeof IntakeHandler];

/**
 * Type for intake status values
 */
export type IntakeStatusValue =
  (typeof IntakeStatus)[keyof typeof IntakeStatus];

/**
 * Array of all valid intake status values
 */
export const ALL_INTAKE_STATUSES: readonly IntakeStatusValue[] =
  Object.values(IntakeStatus);

/**
 * Status count returned by the backend
 */
export interface IntakeStatusCount {
  status: IntakeStatusValue;
  count: number;
}

/**
 * Filter option for intake list dropdowns
 */
export interface IntakeFilterOption {
  id: string;
  name: string;
}

/**
 * Metadata for intake list page (status counts, filter options)
 */
export interface IntakeListMetadata {
  status_counts: IntakeStatusCount[];
  firms: IntakeFilterOption[];
  creators: IntakeFilterOption[];
  assigned_users: IntakeFilterOption[];
}

export interface EmergencyContact {
  name: string;
  phone: string;
  relationship: string;
}

export interface PersonalInfo {
  full_name: string;
  dob: string;
  ssn_last_4: string;
  phone: string;
  email: string;
  address: string;
  preferred_contact: string;
  best_time_to_reach: string;
  preferred_language?: string;
  emergency_contact: EmergencyContact;
  drivers_license_number: string;
  drivers_license_state: string;
}

export interface IncidentInfo {
  date: string;
  time: string;
  location: string;
  state: string;
  weather: string;
  from_to: string;
  type: string;
  description: string;
  present_complaints: string;
  ems: boolean;
  hospital: string;
  currently_treating: boolean;
  police_report: boolean;
  police_dept: string;
  police_report_number: string;
  photos: boolean;
  witnesses: string;
  injured_body_parts?: string[];
  pain_level?: number | null;
}

export interface PIPInsurance {
  company_name: string;
  policy_holder_name: string;
  policy_number: string;
  claim_number: string;
  // Optional directory selections persisted with PIP to aid conversion
  carrier_v2_id?: string | null;
  carrier_division_id?: string | null;
  drivers_license_number?: string;
  drivers_license_state?: string;
  make_model_year?: string;
  license_plate?: string;
}

export interface Defendant {
  name: string;
  address: string;
  drivers_license_number: string;
  drivers_license_state: string;
  drivers_license_plate: string;
  insurance_company_name?: string;
  policy_holder_name?: string;
  policy_number?: string;
  claim_number?: string;
  make_model_year?: string;
  // Optional directory selections for defendant carrier
  carrier_v2_id?: string | null;
  carrier_division_id?: string | null;
}

export interface Insurance {
  defendants: Defendant[];
  pip_insurance: PIPInsurance;
  health_provider: string;
  health_type: string;
  health_member_id: string;
  health_group_id: string;
}

export interface Treatment {
  provider: string;
  date_of_service: string;
  treatment_received: string;
  still_treating: boolean;
}

export interface MedicalWageHistory {
  treatments: Treatment[];
  has_lost_wages: boolean;
  employer: string;
  job_title: string;
  hourly_wage: string;
  employment_duration: string;
  prior_medical_history: string;
}

export interface LegalLifestyle {
  prior_legal_history: string;
  lifestyle_impact: string;
  pending_bills: string;
  out_of_pocket_expenses: string;
  other_notes: string;
}

export interface ReferralSource {
  description: string;
}
// Evidence Review – reusable types
export type EvidenceStatusValue =
  | 'CONFIRMED'
  | 'EXISTS_NOT_HAVE'
  | 'DOES_NOT_EXIST'
  | 'UNCERTAIN';

export type EvidenceStatus = {
  status: EvidenceStatusValue;
  notes: string;
};

export type EvidenceRowKey =
  | 'photos'
  | 'witnesses'
  | 'police_report'
  | 'ems'
  | 'medical_treatment'
  | 'first_party'
  | 'health'
  | 'third_party';

export type EvidenceStatuses = Partial<Record<EvidenceRowKey, EvidenceStatus>>;

// Main form_data object returned by the backend
export type NoteEntry = {
  at: string;
  text: string;
  author_id?: string;
  author_name?: string;
};

// Companion cases types
export interface CompanionRow {
  client_name: string;
  details?: string | null;
  created_intake_id?: string | null;
}

export interface CompanionSet {
  members: CompanionMember[];
}

// Screening-related types
export type ScreeningAnswerValue = 'yes' | 'no' | 'unknown';
export interface ScreeningData {
  // Screening-specific data (not duplicated elsewhere)
  answers: Record<string, ScreeningAnswerValue>;
  // Text fields for sub-questions (e.g., police report details, insurance info)
  // Structure: { questionId: { fieldLabel: value } }
  text_fields?: Record<string, Record<string, string>>;
  notes: string;
  in_progress: boolean;
  intaker_id?: string;
  intaker_name?: string;
  // DEPRECATED: These fields duplicate personal_info/incident_info.
  // Kept for backwards compatibility. New code should read/write
  // directly to personal_info and incident_info instead.
  /** @deprecated Use personal_info.full_name instead */
  client_name?: string;
  /** @deprecated Use personal_info.email instead */
  client_email?: string;
  /** @deprecated Use incident_info.type instead */
  case_type?: string;
  /** @deprecated Use incident_info.date instead */
  loss_date?: string;
  /** @deprecated Use incident_info.state instead */
  state?: string;
}

// V2 Screening types for streamlined qualification flow
export type ScreeningV2QualificationAnswer = 'yes' | 'no' | 'unknown';
export type ScreeningV2QualificationOutcome =
  | 'qualified'
  | 'not_qualified'
  | 'pending';
export type ScreeningV2TranscriptStatus =
  | 'waiting'
  | 'processing'
  | 'complete'
  | 'no_call_found'
  | 'failed';
export type ScreeningV2AIFieldStatus =
  | 'extracted'
  | 'confirmed'
  | 'edited'
  | 'rejected';

export interface ScreeningV2AIExtractedField {
  value: string;
  confidence: number;
  source_quote: string | null;
  status: ScreeningV2AIFieldStatus;
}

export interface ScreeningV2Data {
  version: 'v2';
  current_screen: 1 | 2 | 3 | 4;
  // Screen 1: Qualification
  qualification_answers: Record<string, ScreeningV2QualificationAnswer>;
  qualification_context_asked: Record<string, boolean>;
  qualification_outcome: ScreeningV2QualificationOutcome;
  qualification_completed_at: string | null;
  retainer_sent_at: string | null;
  // Screen 2: Data Collection
  collected_fields: string[];
  collection_completed_at: string | null;
  // Screen 3: AI Review
  transcript_status: ScreeningV2TranscriptStatus;
  matched_call_id: string | null;
  ai_extracted_fields: Record<string, ScreeningV2AIExtractedField>;
  human_reviewed_fields: Record<string, string>;
  review_completed_at: string | null;
  // Metadata
  intaker_id: string | null;
  intaker_name: string;
}

export interface IntakeFormData {
  loe_status: string;
  attorney_decision: string;
  rejection_reason: string;
  hipaa_status: string;
  declaration_status: string;
  insurance_status: string;
  // Legacy single-string note. Intentionally optional so PATCH bodies can omit it
  // and avoid clobbering existing legacy content.
  general_notes?: string;
  // New structured notes timeline (newest last by convention; UI can sort)
  general_notes_log?: NoteEntry[];
  evidence_statuses?: EvidenceStatuses;
  personal_info: PersonalInfo;
  incident_info: IncidentInfo;
  insurance: Insurance;
  medical_wage_history: MedicalWageHistory;
  legal_lifestyle: LegalLifestyle;
  referral_source: ReferralSource;
  lead_source?: string;
  schema_version: string;
  companions?: CompanionRow[];
  // Screening sheet data (optional - only set during screening flow)
  screening?: ScreeningData;
  // V2 Screening data (optional - only set for new intakes using V2 flow)
  screening_v2?: ScreeningV2Data;
  // Provider service IDs added during screening (converted to MedicalProviders on matter creation)
  provider_service_ids?: string[];
}

export interface IntakeSummary {
  green_flags: string;
  yellow_flags: string;
  red_flags: string;
  case_summary: string;
  recommendation_decision: string;
  recommendation_reason: string;
  recommendation_counterpoint_heading?: string | null;
  recommendation_counterpoint?: string | null;
}

// First contact outcome (V2 screening) - matches backend FirstContactOutcomeEnum
export type FirstContactOutcome = 'completed' | 'incomplete' | 'unavailable';

// Complete Intake object from API
export interface Intake {
  id: string;
  firm: string;
  firm_name: string;
  creator: string | null;
  creator_name: string;
  assigned_to: string | null;
  assigned_to_name: string;
  status: IntakeStatusValue;
  handler?: IntakeHandlerValue;
  matter_id: string | null;
  matter_status: string | null;
  form_data: IntakeFormData;
  summary?: IntakeSummary | null;
  created_at: string;
  modified_at: string;
  companion_root?: string | null;
  companion_count: number;
  is_companion_primary: boolean;
  manual_qualification?: ManualQualificationEnum | null;
  rejection_email_sent?: boolean;
  is_scheduled?: boolean;
  scheduled_at?: string | null;
  scheduled_assignee?: string | null;
  scheduled_assignee_name?: string;
  scheduled_backup_assignee?: string | null;
  scheduled_backup_assignee_name?: string;
  google_calendar_event_id?: string | null;
  google_calendar_event_link?: string | null;
  // V2 Screening first contact outcome (stored on model, not in form_data)
  first_contact_outcome?: FirstContactOutcome | null;
  // Reason for client being unavailable (when first_contact_outcome = 'unavailable')
  unavailable_reason?: string | null;
  // Timestamp when End Intake was clicked
  end_intake_at?: string | null;
  // Timestamp when a call was made to the client
  called_client_at?: string | null;
  // No matter needed flag
  no_matter_needed?: boolean;
}

export enum ManualQualificationEnum {
  QUALIFIED = 'qualified',
  UNQUALIFIED = 'unqualified',
  UNKNOWN = 'unknown',
}
// For creating a new intake
export interface CreateIntakePayload {
  firm: string;
  creator?: string | null;
  personal_info?: Partial<PersonalInfo> | null;
  incident_info?: Partial<IncidentInfo> | null;
}

// For updating an intake via PATCH endpoint
// Note: status and handler are NOT included - they have dedicated endpoints:
// - status: POST /api/intakes/{id}/status/ (useIntakesSetStatusCreateMutation)
// - handler: POST /api/intakes/{id}/handler/ (useIntakesSetHandlerCreateMutation)
export interface UpdateIntakePayload {
  id: string;
  form_data?: Partial<IntakeFormData> | null;
  summary?: IntakeSummary | null;
  firm?: string;
  assigned_to?: string | null;
  manual_qualification?: ManualQualificationEnum | null;
  rejection_email_sent?: boolean;
  is_scheduled?: boolean;
  skipOptimistic?: boolean; // Flag to control optimistic updates (not sent to backend)
}

// Schedule callback payload for the dedicated schedule endpoint
export interface ScheduleCallbackPayload {
  intakeId: string;
  scheduled_at: string;
  scheduled_assignee_id: string;
  scheduled_backup_assignee_id?: string;
  skip_calendar?: boolean;
}

// For list view (simplified intake data)
export interface IntakeListItem {
  id: string;
  firm_id: string;
  firm_name: string;
  creator_name: string;
  assigned_to: string | null;
  assigned_to_name: string;
  status: IntakeStatusValue;
  handler?: IntakeHandlerValue;
  client_name: string;
  client_phone: string;
  incident_type: string;
  attorney_decision: string;
  loe_status: string;
  created_at: string;
  modified_at: string;
  referral_source_description?: string;
  rejection_reason?: string;
  rejection_email_sent?: boolean;
  lead_source?: string;
  latest_note: string;
  latest_note_at: string;
  notes_count: number;
  companion_count: number;
  is_companion_primary: boolean;
  manual_qualification?: ManualQualificationEnum | null;
  has_matter?: boolean | null;
  matter_status?: string | null;
  is_scheduled?: boolean;
  scheduled_at?: string | null;
  scheduled_assignee_name?: string;
  scheduled_backup_assignee_name?: string;
  /** PNC's US state abbreviation (e.g. "WA") — used for timezone-aware callability */
  incident_state?: string | null;
  /** Backend-computed callability group (only present in chase views) */
  callability_group?: string | null;
}

// Filters for intake queries
export interface IntakeFilters {
  firm?: string;
  status?: IntakeStatusValue;
  created_after?: string;
  created_before?: string;
  search?: string;
}

// Export-related types
export interface StartExportResponse {
  export_id: string;
  status: 'PENDING' | 'GENERATING' | 'COMPLETED' | 'FAILED';
  message: string;
  workflow_id?: string;
}

export interface ExportStatusResponse {
  export_id: string;
  status: 'PENDING' | 'GENERATING' | 'COMPLETED' | 'FAILED' | 'no_export';
  created_at?: string;
  download_url?: string;
  message?: string;
}
